import React, { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { toast } from 'react-toastify';
import api, { STORAGE_URL } from '../../api/axios';
import { FiPlus, FiEdit2, FiTrash2, FiTag, FiChevronDown, FiChevronRight } from 'react-icons/fi';
import { dt, DashLang } from '../../utils/dashboardTranslations';

export default function Categories() {
  const { store, lang } = useOutletContext<{ store: any; lang: DashLang }>();
  const [categories, setCategories] = useState<any[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [image, setImage] = useState<File | null>(null);
  const [form, setForm] = useState<{
    name: string;
    name_ar: string;
    description: string;
    sort_order: number | '';
    parent_id: string;
    is_active: boolean;
  }>({ name: '', name_ar: '', description: '', sort_order: 0, parent_id: '', is_active: true });
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (store?.id) loadCategories();
  }, [store?.id]);

  const loadCategories = async () => {
    setLoading(true);
    try { const res = await api.get(`/stores/${store.id}/categories`); setCategories(res.data); }
    finally { setLoading(false); }
  };

  const toggleExpand = (id: number) => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const openModal = (cat?: any, parentId?: number) => {
    if (cat) {
      setEditing(cat);
      setForm({
        name: cat.name,
        name_ar: cat.name_ar || '',
        description: cat.description || '',
        sort_order: cat.sort_order ?? 0,
        parent_id: cat.parent_id ? String(cat.parent_id) : '',
        is_active: cat.is_active !== false,
      });
    } else {
      setEditing(null);
      setForm({ name: '', name_ar: '', description: '', sort_order: 0, parent_id: parentId ? String(parentId) : '', is_active: true });
    }
    setImage(null);
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData();
    Object.entries(form).forEach(([key, val]) => {
      if (key === 'is_active') {
        formData.append(key, val ? '1' : '0');
      } else if (key === 'sort_order') {
        // Allow clearing the input in the UI; empty means 0. Always send the
        // number so Laravel receives an explicit sort (including 0).
        const n = val === '' || val === null || val === undefined ? 0 : Number(val);
        formData.append(key, String(Number.isFinite(n) ? n : 0));
      } else if (val !== '' && val !== null && val !== undefined) {
        formData.append(key, String(val));
      }
    });
    if (image) formData.append('image', image);

    try {
      if (editing) {
        formData.append('_method', 'PUT');
        await api.post(`/stores/${store.id}/categories/${editing.id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        toast.success(dt('category_updated', lang));
      } else {
        await api.post(`/stores/${store.id}/categories`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        toast.success(dt('category_created', lang));
      }
      setShowModal(false);
      loadCategories();
    } catch (err: any) {
      toast.error(err.response?.data?.message || dt('error_saving_category', lang));
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm(dt('delete_category_confirm', lang))) return;
    try {
      await api.delete(`/stores/${store.id}/categories/${id}`);
      toast.success(dt('category_deleted', lang));
      loadCategories();
    } catch {
      toast.error(dt('error_deleting_category', lang));
    }
  };

  // Flatten parent categories for the dropdown (exclude current editing + its children)
  const parentOptions = categories.filter(c => !editing || c.id !== editing.id);

  if (!store) return null;

  const t = (en: string, ar: string) => lang === 'ar' ? ar : en;

  const toggleActive = async (cat: any) => {
    try {
      await api.put(`/stores/${store.id}/categories/${cat.id}`, { is_active: !cat.is_active });
      loadCategories();
    } catch {
      toast.error(dt('error_saving_category', lang));
    }
  };

  const renderCategoryRow = (cat: any, depth: number = 0) => {
    const hasChildren = cat.children && cat.children.length > 0;
    const isExpanded = expanded[cat.id];
    const totalProducts = (cat.products_count || 0) + (cat.children || []).reduce((sum: number, c: any) => sum + (c.products_count || 0), 0);

    return (
      <React.Fragment key={cat.id}>
        <tr style={{ opacity: cat.is_active === false ? 0.5 : 1 }}>
          <td>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingInlineStart: depth * 28 }}>
              {hasChildren ? (
                <button
                  type="button"
                  onClick={() => toggleExpand(cat.id)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'var(--gray-500)', display: 'flex' }}
                >
                  {isExpanded ? <FiChevronDown size={16} /> : <FiChevronRight size={16} />}
                </button>
              ) : (
                <span style={{ width: 20 }} />
              )}
              {cat.image ? (
                <img src={`${STORAGE_URL}/${cat.image}`} alt="" style={{ width: 40, height: 40, borderRadius: 8, objectFit: 'cover' }} />
              ) : (
                <div style={{ width: 40, height: 40, borderRadius: 8, background: depth > 0 ? 'var(--gray-50)' : 'var(--gray-100)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <FiTag size={14} color="var(--gray-400)" />
                </div>
              )}
            </div>
          </td>
          <td>
            <div style={{ paddingInlineStart: depth * 28 }}>
              <strong style={{ fontSize: depth > 0 ? 13 : 14 }}>{cat.name}</strong>
              {cat.name_ar && <div style={{ fontSize: 12, color: 'var(--gray-400)', direction: 'rtl' }}>{cat.name_ar}</div>}
              {depth > 0 && (
                <span style={{ fontSize: 11, color: 'var(--gray-400)' }}>
                  {t('Sub of', 'فرعي من')} {categories.find(p => p.id === cat.parent_id)?.name || ''}
                </span>
              )}
            </div>
          </td>
          <td>
            <span style={{ fontSize: 13 }}>
              {cat.products_count || 0}
              {hasChildren && depth === 0 && (
                <span style={{ color: 'var(--gray-400)', fontSize: 11 }}> ({totalProducts} {t('total', 'إجمالي')})</span>
              )}
            </span>
          </td>
          <td>
            {hasChildren && depth === 0 && (
              <span style={{
                fontSize: 11, padding: '2px 8px', borderRadius: 50,
                background: 'var(--primary-bg, #eff6ff)', color: 'var(--primary, #2563eb)',
              }}>
                {cat.children.length} {t('sub', 'فرعي')}
              </span>
            )}
          </td>
          <td>{cat.sort_order ?? 0}</td>
          <td>
            <div
              onClick={() => toggleActive(cat)}
              style={{
                width: 36, height: 20, borderRadius: 10,
                background: cat.is_active !== false ? 'var(--primary, #2563eb)' : 'var(--gray-300)',
                position: 'relative', transition: 'background 0.2s', cursor: 'pointer',
              }}
            >
              <div style={{
                width: 16, height: 16, borderRadius: '50%', background: '#fff',
                position: 'absolute', top: 2,
                left: cat.is_active !== false ? 18 : 2,
                transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
              }} />
            </div>
          </td>
          <td>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <button className="btn btn-sm btn-white" style={{ padding: '6px 10px' }} onClick={() => openModal(cat)}><FiEdit2 size={14} /></button>
              <button className="btn btn-sm btn-white" style={{ padding: '6px 10px', color: 'var(--danger)' }} onClick={() => handleDelete(cat.id)}><FiTrash2 size={14} /></button>
              {depth === 0 && (
                <button
                  className="btn btn-sm btn-white"
                  onClick={() => openModal(undefined, cat.id)}
                  title={t('Add Subcategory', 'إضافة فرعي')}
                  style={{ padding: '6px 10px' }}
                >
                  <FiPlus size={14} />
                </button>
              )}
            </div>
          </td>
        </tr>
        {hasChildren && isExpanded && cat.children.map((child: any) => renderCategoryRow(child, depth + 1))}
      </React.Fragment>
    );
  };

  return (
    <div>
      <div className="page-header">
        <h1>{dt('categories', lang)}</h1>
        <button className="btn btn-primary" onClick={() => openModal()}>
          <FiPlus /> {dt('add_category', lang)}
        </button>
      </div>

      {loading ? (
        <div className="empty-state"><p>{dt('loading', lang)}...</p></div>
      ) : categories.length === 0 ? (
        <div className="empty-state">
          <FiTag />
          <h3>{dt('no_categories', lang)}</h3>
          <p>{dt('add_first_category', lang)}</p>
          <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={() => openModal()}>
            <FiPlus /> {dt('add_category', lang)}
          </button>
        </div>
      ) : (
        <div className="data-table">
          <table>
            <thead>
              <tr>
                <th style={{ width: 80 }}>{dt('image', lang)}</th>
                <th>{dt('name', lang)}</th>
                <th>{dt('total_products', lang)}</th>
                <th>{t('Subcategories', 'فرعيات')}</th>
                <th>{dt('sort_order', lang)}</th>
                <th>{dt('status', lang)}</th>
                <th>{dt('actions', lang)}</th>
              </tr>
            </thead>
            <tbody>
              {categories.map((cat) => renderCategoryRow(cat, 0))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>{editing ? dt('edit_category', lang) : form.parent_id ? t('Add Subcategory', 'إضافة فرعي') : dt('add_category', lang)}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">{t('Parent Category', 'الفئة الرئيسية')}</label>
                <select
                  className="form-input"
                  value={form.parent_id}
                  onChange={(e) => setForm({ ...form, parent_id: e.target.value })}
                >
                  <option value="">{t('None (Top Level)', 'بدون (فئة رئيسية)')}</option>
                  {parentOptions.map(c => (
                    <option key={c.id} value={c.id}>{c.name}{c.name_ar ? ` — ${c.name_ar}` : ''}</option>
                  ))}
                </select>
              </div>
              <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div className="form-group">
                  <label className="form-label">{dt('name_en', lang)} *</label>
                  <input className="form-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label className="form-label">{dt('name_ar', lang)}</label>
                  <input className="form-input" value={form.name_ar} onChange={(e) => setForm({ ...form, name_ar: e.target.value })} dir="rtl" />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">{dt('description', lang)}</label>
                <textarea className="form-input" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
              <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div className="form-group">
                  <label className="form-label">{dt('sort_order', lang)}</label>
                  <input
                    type="number"
                    className="form-input"
                    value={form.sort_order}
                    onChange={(e) => {
                      const raw = e.target.value;
                      setForm({
                        ...form,
                        // Keep '' while the user clears the field; don't force 0.
                        sort_order: raw === '' ? '' : (Number.isNaN(parseInt(raw, 10)) ? '' : parseInt(raw, 10)),
                      });
                    }}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">{dt('image', lang)}</label>
                  <input type="file" accept="image/*" onChange={(e) => setImage(e.target.files?.[0] || null)} className="form-input" />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">{dt('status', lang)}</label>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, is_active: true })}
                    style={{
                      flex: 1, padding: '8px 12px', borderRadius: 8, border: '2px solid',
                      borderColor: form.is_active ? '#16a34a' : 'var(--gray-200)',
                      background: form.is_active ? '#dcfce7' : 'white',
                      color: form.is_active ? '#16a34a' : 'var(--gray-400)',
                      cursor: 'pointer', fontWeight: 600, fontSize: 13, fontFamily: 'inherit',
                    }}
                  >
                    {dt('active', lang)}
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, is_active: false })}
                    style={{
                      flex: 1, padding: '8px 12px', borderRadius: 8, border: '2px solid',
                      borderColor: !form.is_active ? '#dc2626' : 'var(--gray-200)',
                      background: !form.is_active ? '#fee2e2' : 'white',
                      color: !form.is_active ? '#dc2626' : 'var(--gray-400)',
                      cursor: 'pointer', fontWeight: 600, fontSize: 13, fontFamily: 'inherit',
                    }}
                  >
                    {dt('inactive', lang)}
                  </button>
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-white" onClick={() => setShowModal(false)}>{dt('cancel', lang)}</button>
                <button type="submit" className="btn btn-primary">{editing ? dt('update', lang) : dt('create', lang)}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
